import React, { useEffect, useRef, useState } from 'react';
import {
  ShieldCheck, KeyRound, FileBadge, Upload, Loader2, CheckCircle,
  AlertTriangle, Plug, Eye, EyeOff,
} from 'lucide-react';
import {
  getKsefSettings, updateKsefSettings, uploadKsefCertificate, enrollKsefCertificate,
  testKsefConnection, disconnectKsef, parseKsefError,
  KsefConnection, KsefEnvironment, KsefAuthMethod, KSEF_ENV_META,
} from '../../../api/ksef';
import { KsefEnvBadge } from './KsefChip';

const KsefSettingsCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [sellerId, setSellerId] = useState<number | undefined>();
  const [ksef, setKsef] = useState<KsefConnection | null>(null);
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [certPassword, setCertPassword] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollName, setEnrollName] = useState('ViewPoint KSeF certificate');
  const [enrollPassword, setEnrollPassword] = useState('');
  const enrollFileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const res = await getKsefSettings();
      setSellerId(res.seller_id);
      setKsef(res.ksef);
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const flash = (msg: string) => { setOk(msg); setError(null); setTimeout(() => setOk(null), 4000); };

  const patch = async (payload: any, label: string) => {
    setBusy(label); setError(null);
    try {
      const res = await updateKsefSettings({ seller_id: sellerId, ...payload });
      setKsef(res.ksef);
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setBusy(null);
    }
  };

  const onUploadCert = async (file: File) => {
    setBusy('cert'); setError(null);
    try {
      const res = await uploadKsefCertificate(file, certPassword, sellerId);
      setKsef(res.ksef);
      flash('Certificate loaded.');
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setBusy(null);
    }
  };

  const onEnroll = async (file: File) => {
    setBusy('enroll'); setError(null);
    try {
      const res = await enrollKsefCertificate(file, enrollPassword, enrollName, sellerId);
      setKsef(res.ksef);
      setEnrollOpen(false);
      setEnrollPassword('');
      flash('KSeF certificate issued and stored. You can now Test connection.');
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setBusy(null);
    }
  };

  const onTest = async () => {
    setBusy('test'); setError(null);
    try {
      const res = await testKsefConnection(sellerId);
      setKsef(res.ksef);
      flash('Connected to KSeF — credentials verified.');
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setBusy(null);
    }
  };

  const onDisconnect = async () => {
    setBusy('disconnect'); setError(null);
    try {
      const res = await disconnectKsef(sellerId);
      setKsef(res.ksef);
      setToken(''); setCertPassword('');
      flash('Disconnected from KSeF.');
    } catch (e: any) {
      setError(await parseKsefError(e));
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-stone-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading KSeF settings…
      </div>
    );
  }
  if (!ksef) return null;

  const method = ksef.auth_method;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-stone-900 dark:text-white flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-coral-600" /> KSeF connection
            <KsefEnvBadge env={ksef.environment} />
          </h2>
          <p className="text-sm text-stone-500">
            Authorize ViewPoint to submit FA(3) e-invoices to the Polish Ministry of Finance.
          </p>
        </div>
        {ksef.connected && (
          <button
            onClick={onDisconnect}
            disabled={!!busy}
            className="text-sm px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-50 dark:border-stone-600"
          >
            {busy === 'disconnect' ? '…' : 'Disconnect'}
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}
      {ok && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm border border-green-200">
          <CheckCircle className="w-4 h-4" /> {ok}
        </div>
      )}

      {ksef.connected && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <div className="text-sm text-green-800">
            <b>Connected to KSeF.</b>{' '}
            {method === 'token' ? 'Authorization token' : 'Certificate'} ·{' '}
            {KSEF_ENV_META[ksef.environment].label}
            {ksef.connected_at ? ` · verified ${new Date(ksef.connected_at).toLocaleString()}` : ''}
          </div>
        </div>
      )}

      {/* Environment */}
      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
          Environment
        </label>
        <div className="inline-flex rounded-lg border border-stone-300 dark:border-stone-600 overflow-hidden">
          {(['test', 'demo', 'prod'] as KsefEnvironment[]).map((e) => (
            <button
              key={e}
              onClick={() => patch({ environment: e }, 'env')}
              disabled={!!busy}
              className={`px-4 py-2 text-sm font-medium ${
                ksef.environment === e
                  ? 'bg-coral-600 text-white'
                  : 'bg-white dark:bg-stone-800 text-stone-600 hover:bg-stone-50'
              }`}
            >
              {KSEF_ENV_META[e].label}
            </button>
          ))}
        </div>
        <p className="text-xs text-stone-500 mt-2">
          {KSEF_ENV_META[ksef.environment].host} — {KSEF_ENV_META[ksef.environment].legal}
        </p>
      </div>

      {/* Auth method */}
      <div>
        <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1.5">
          Authentication method
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {([
            ['token', KeyRound, 'Authorization token', 'Automated · system submits unattended'],
            ['certificate', FileBadge, 'KSeF certificate', 'Manual · cert + key + password (Fakturownia-style)'],
          ] as [KsefAuthMethod, any, string, string][]).map(([m, Icon, title, sub]) => (
            <button
              key={m}
              onClick={() => patch({ auth_method: m }, 'method')}
              disabled={!!busy}
              className={`text-left p-3 rounded-xl border transition ${
                method === m
                  ? 'border-coral-500 ring-2 ring-coral-200 bg-coral-50/40'
                  : 'border-stone-200 dark:border-stone-700 hover:border-stone-300'
              }`}
            >
              <div className="flex items-center gap-2 font-medium text-stone-800 dark:text-stone-200">
                <Icon className="w-4 h-4 text-coral-600" /> {title}
              </div>
              <div className="text-xs text-stone-500 mt-1">{sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Token branch */}
      {method === 'token' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-stone-700 dark:text-stone-300">
            KSeF 2.0 authorization token
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder={ksef.has_token ? '•••••••••• (token saved)' : 'Paste your KSeF 2.0 token'}
                className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 dark:bg-stone-800 pr-16"
              />
              <button
                type="button"
                onClick={() => setShowToken((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400"
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => patch({ token, auth_method: 'token' }, 'token')}
              disabled={!!busy || !token.trim()}
              className="px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium disabled:opacity-50"
            >
              {busy === 'token' ? '…' : 'Save token'}
            </button>
          </div>
          <p className="text-xs text-stone-500">
            Generate a token in the KSeF 2.0 Taxpayer App. KSeF 1.0 tokens are not accepted.
            It is encrypted at rest and never shown again.
          </p>
          <div className="flex gap-2">
            {(['issue', 'view', 'admin'] as const).map((s) => (
              <button
                key={s}
                onClick={() => patch({ token_scope: s }, 'scope')}
                disabled={!!busy}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  ksef.token_scope === s
                    ? 'border-coral-500 bg-coral-50 text-coral-700'
                    : 'border-stone-300 text-stone-600'
                }`}
              >
                {s === 'issue' ? 'Issue invoices' : s === 'view' ? 'View invoices' : 'Full admin'}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Certificate branch */}
      {method === 'certificate' && (
        <div className="space-y-3">
          {ksef.has_certificate && ksef.certificate ? (
            <div className="p-4 rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50/60 text-sm">
              <div className="font-semibold text-stone-800 dark:text-stone-200">
                {ksef.certificate.subject || 'Certificate'}
              </div>
              <div className="text-xs text-stone-500 mt-1 space-y-0.5">
                {ksef.certificate.subject_nip && <div>NIP {ksef.certificate.subject_nip}</div>}
                <div>Issuer: {ksef.certificate.issuer || '—'}</div>
                <div>Serial: {ksef.certificate.serial}</div>
                <div className="break-all">SHA-256: {ksef.certificate.fingerprint}</div>
                <div>
                  Valid: {ksef.certificate.valid_from?.slice(0, 10)} →{' '}
                  {ksef.certificate.valid_to?.slice(0, 10)}
                </div>
              </div>
            </div>
          ) : null}

          <div
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed border-stone-300 dark:border-stone-600 cursor-pointer hover:border-coral-400"
          >
            {busy === 'cert' ? (
              <Loader2 className="w-5 h-5 animate-spin text-coral-600" />
            ) : (
              <Upload className="w-5 h-5 text-stone-400" />
            )}
            <div className="text-sm text-stone-600">
              {ksef.has_certificate ? 'Replace certificate' : 'Drop or click to upload .p12 / .pfx'}
            </div>
            <div className="text-xs text-stone-400">PKCS#12 (KSeF or qualified cert + key) · up to 2 MB</div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".p12,.pfx"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && onUploadCert(e.target.files[0])}
          />
          <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-3">
            <button
              type="button"
              onClick={() => setEnrollOpen((o) => !o)}
              className="text-sm font-medium text-coral-700 dark:text-foreground-muted"
            >
              {enrollOpen ? '▾' : '▸'} Don’t have a KSeF certificate yet? Request one
            </button>
            {enrollOpen && (
              <div className="mt-3 space-y-3">
                <p className="text-xs text-stone-500">
                  One-time bootstrap: upload your <b>qualified signature</b> (.p12/.pfx) — it
                  authenticates you to KSeF once, a KSeF certificate is issued, and that
                  becomes this profile’s permanent credential. The qualified file is used
                  transiently and never stored.
                </p>
                <input
                  value={enrollName}
                  onChange={(e) => setEnrollName(e.target.value)}
                  placeholder="Certificate name"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 dark:bg-stone-800 text-sm"
                />
                <input
                  type="password"
                  value={enrollPassword}
                  onChange={(e) => setEnrollPassword(e.target.value)}
                  placeholder="Qualified certificate password"
                  className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 dark:bg-stone-800 text-sm"
                />
                <button
                  onClick={() => enrollFileRef.current?.click()}
                  disabled={!!busy}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {busy === 'enroll' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBadge className="w-4 h-4" />}
                  Upload qualified cert & request KSeF certificate
                </button>
                <input
                  ref={enrollFileRef}
                  type="file"
                  accept=".p12,.pfx"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && onEnroll(e.target.files[0])}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
              Certificate password
            </label>
            <input
              type="password"
              value={certPassword}
              onChange={(e) => setCertPassword(e.target.value)}
              placeholder="••••••••••"
              className="w-full px-3 py-2 rounded-lg border border-stone-300 dark:border-stone-600 dark:bg-stone-800"
            />
            <p className="text-xs text-stone-500 mt-1">
              Used to decrypt the private key. Stored encrypted with the app secret — never sent in plain text.
            </p>
          </div>
          <div className="flex gap-2">
            {(['each', 'session'] as const).map((u) => (
              <button
                key={u}
                onClick={() => patch({ unlock_mode: u }, 'unlock')}
                disabled={!!busy}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                  ksef.unlock_mode === u
                    ? 'border-coral-500 bg-coral-50 text-coral-700'
                    : 'border-stone-300 text-stone-600'
                }`}
              >
                {u === 'each' ? 'Ask each submission' : 'Keep unlocked this session'}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-stone-200 dark:border-stone-700">
        <button
          onClick={onTest}
          disabled={!!busy || !method}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium disabled:opacity-50"
        >
          {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
          Test connection
        </button>
      </div>
    </div>
  );
};

export default KsefSettingsCard;
